import { useMemo, useState, useCallback } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';

import { zonesTableTabsTableColumns } from 'configs';
import { ROUTES } from 'consts';
import { EZoneStatus } from 'enums';
import { IEnhancedTableHeadCell, IZone, IZoneWithActions } from 'models';
import {
  useFetchZoneQuery,
  useFetchZonesQuery,
  useLazyDownloadDecalQuery,
  usePublicFetchZoneLogQuery,
  usePublicFetchZoneQuery,
} from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { useLoadingToast } from '../../hooks/toasts/loading-toast.hook';
import { filterZoneItems } from 'utils';

type BuildingId = number | null;

type UseZonesProps = {
  buildingId?: BuildingId;
};

export const useZones = (props: UseZonesProps) => {
  const { buildingId } = props;
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data, isLoading } = useFetchZonesQuery({ orgSlug });

  let zones = (data || []).map((zone: IZone) => ({
    ...zone,
    floorInfo: zone?.floor?.floor || `${zone.building_floor_id}`,
    rating: zone.rating || 0,
    lastServicedBy: (zone.logs || []).length > 0 ? zone.logs[0]?.day_porter?.name || '' : '',
    serviceItemsText: (zone?.service_items || []).map((serviceItem) => serviceItem.name).join(', '),
  }));

  if (buildingId) {
    zones = zones.filter((zone: IZone) => zone.building_id === buildingId);
  }

  const activeZones = zones.filter((zone: any) => zone.status === EZoneStatus.ACTIVE);
  const pendingZones = zones.filter((zone: any) => zone.status === EZoneStatus.PENDING);
  const inactiveZones = zones.filter((zone: any) => zone.status === EZoneStatus.INACTIVE);

  return useMemo(
    () =>
      [
        isLoading,
        {
          all: zones,
          active: activeZones,
          pending: pendingZones,
          inactive: inactiveZones,
        },
      ] as [
        boolean,
        {
          all: IZone[];
          active: IZone[];
          pending: IZone[];
          inactive: IZone[];
        },
      ],
    [isLoading, zones, activeZones, pendingZones, inactiveZones],
  );
};

type UseZoneProps = {
  zoneId: number;
};

export const useZone = ({ zoneId }: UseZoneProps): [boolean, IZone] => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);

  const { data, isLoading } = useFetchZoneQuery({ orgSlug, id: zoneId });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

type UsePublicZoneProps = {
  code: string;
};

export const usePublicZone = ({ code }: UsePublicZoneProps): [boolean, IZone] => {
  const { data, isLoading } = usePublicFetchZoneQuery({ code });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

type UseZonesCountProps = UseZonesProps;

export const usePublicZoneLogs = ({ zoneId, limit }: UsePublicZoneLogProps): [boolean, any[]] => {
  const { data, isLoading } = usePublicFetchZoneLogQuery({ zoneId, limit });

  return useMemo(() => [isLoading, data], [isLoading, data]);
};

type UsePublicZoneLogProps = {
  zoneId: number;
  limit: number;
};

export const useZonesCount = ({
  buildingId,
}: UseZonesCountProps): {
  loading: boolean;
  active: number;
  pending: number;
  inactive: number;
} => {
  const [loading, { active: activeZones, pending: pendingZones, inactive: inactiveZones }] = useZones({ buildingId });

  const active = activeZones.length;
  const pending = pendingZones.length;
  const inactive = inactiveZones.length;

  return { loading, active, pending, inactive };
};

type UseZonesTableRowsProps = {
  loading: boolean;
  setState?: (state: any) => void;
  zones: IZone[];
};

export const useZonesTableRows = ({
  setState = () => {},
  zones,
  loading,
}: UseZonesTableRowsProps): [boolean, IZoneWithActions[]] => {
  const navigate = useNavigate();
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const [downloadDecalTrigger] = useLazyDownloadDecalQuery();

  const viewCleancode = (zone: IZone) => window.open(`${process.env.REACT_APP_CLEANCODE_URL}/${zone.code}`);
  const manageServiceItem = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneServiceItemsDialog: true }));
  const viewCleaningHistory = (zone: IZone) =>
    navigate(`/${orgSlug}/${ROUTES.ZONE_CLEANING_HISTORY.replace(':zoneId', `${zone.id}`)}`);
  const copyZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneCopyDialog: true }));
  const editZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneUpdateDialog: true }));
  const deactivateZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneDeactivateDialog: true }));
  const purchaseZone = () => navigate(`/${orgSlug}/${ROUTES.CART}`);
  const deleteZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneDeleteDialog: true }));
  const activateZone = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleZoneActivateDialog: true }));
  const suspendService = (zone: IZone) =>
    setState((prev: any) => ({ ...prev, zoneId: zone.id, visibleSuspendServiceDialog: true }));
  const downloadDecal = async (zone: IZone) => {
    const loadingToast = useLoadingToast();
    try {
      loadingToast.open({ message: 'Downloading Decal' });
      const result = await downloadDecalTrigger({ orgSlug, id: zone.id, format: 'pdf' });
      if (result.isSuccess) {
        const decalPdfBlob = new Blob([result.data], { type: 'application/pdf' });
        const downloadUrl = URL.createObjectURL(decalPdfBlob);

        const downloadLink = document.createElement('a');
        downloadLink.target = '_blank';
        downloadLink.download = `zone_${zone.code}_decal.pdf`;

        // set object URL as the anchor's href
        downloadLink.href = downloadUrl;

        // append the anchor to document body
        document.body.append(downloadLink);

        // fire a click event on the anchor
        downloadLink.click();
      }
    } finally {
      loadingToast.close();
    }
  };

  const rows = zones.map(
    (zone: IZone) =>
      ({
        ...zone,
        viewCleancode,
        manageServiceItem,
        viewCleaningHistory,
        copyZone,
        editZone,
        deactivateZone,
        purchaseZone,
        deleteZone,
        activateZone,
        suspendService,
        downloadDecal,
      } as IZoneWithActions),
  );

  return useMemo(() => {
    return [loading, rows];
  }, [loading, rows]);
};

type UseActiveZonesTableRowsProps = UseZonesTableRowsProps;

export const useActiveZonesTableRows = ({
  setState,
  zones,
  loading,
}: UseActiveZonesTableRowsProps): [boolean, string[], IEnhancedTableHeadCell[], IZoneWithActions[]] => {
  const [loadingZonesTableRows, allRows] = useZonesTableRows({ loading, setState, zones });

  const columnKeys = ['code', 'floorInfo', 'name', 'rating', 'serviceItemsText', 'lastServicedBy', 'actions'];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row) => row.status === EZoneStatus.ACTIVE);

  return [loadingZonesTableRows, columnKeys, columns, rows];
};

type UseInactiveZonesTableRows = UseZonesTableRowsProps;

export const useInactiveZonesTableRows = ({
  setState,
  zones,
  loading,
}: UseInactiveZonesTableRows): [boolean, string[], IEnhancedTableHeadCell[], IZoneWithActions[]] => {
  const [loadingZonesTableRows, allRows] = useZonesTableRows({ loading, setState, zones });

  const columnKeys = ['code', 'floorInfo', 'name', 'rating', 'serviceItemsText', 'lastServicedBy', 'actions'];

  const columns = zonesTableTabsTableColumns.filter((column) => columnKeys.includes(column.id));

  const rows = allRows.filter((row) => row.status === EZoneStatus.INACTIVE);

  return [loadingZonesTableRows, columnKeys, columns, rows];
};

type UseZoneItemsProps = {
  buildingId: number | null | undefined;
  activeFilters: FilterState;
  inactiveFilters: FilterState;
};

export const useZoneItems = ({ buildingId = null, activeFilters, inactiveFilters }: UseZoneItemsProps) => {
  const [loading, { active, inactive }] = useZones({ buildingId });

  const filteredActive = useMemo(() => {
    return filterZoneItems(active, {
      searchKeyword: activeFilters.search,
      address: activeFilters.address,
      floor: activeFilters.floor,
      name: activeFilters.name,
    });
  }, [active, activeFilters]);

  const filteredInactive = useMemo(() => {
    return filterZoneItems(inactive, {
      searchKeyword: inactiveFilters.search,
      address: inactiveFilters.address,
      floor: inactiveFilters.floor,
      name: inactiveFilters.name,
    });
  }, [inactive, inactiveFilters]);

  const counts = useMemo(
    () => ({
      active: filteredActive.length,
      inactive: filteredInactive.length,
    }),
    [filteredActive, filteredInactive],
  );

  return {
    loading,
    activeZones: active,
    inactiveZones: inactive,
    filteredActiveZones: filteredActive,
    filteredInactiveZones: filteredInactive,
    counts,
  };
};

export const useActiveZonePage = () => {
  const isActivePage = useMatch(`${ROUTES.ORG_SLUG}/${ROUTES.ZONES}/${ROUTES.ZONES_ACTIVE}`);
  const isInactivePage = useMatch(`${ROUTES.ORG_SLUG}/${ROUTES.ZONES}/${ROUTES.ZONES_INACTIVE}`);

  return {
    isActivePage: !!isActivePage,
    isInactivePage: !!isInactivePage,
    tabKey: isActivePage ? 'active' : isInactivePage ? 'inactive' : null,
  } as { isActivePage: boolean; isInactivePage: boolean; tabKey: 'active' | 'inactive' | null };
};

type TabKey = 'active' | 'inactive';

type FilterState = {
  search: string;
  address: string;
  floor: string;
  name: string;
};

type UseZoneFiltersArgs = {
  tabKey: TabKey | null;
  buildingId?: number | null;
};

const EMPTY_FILTER: FilterState = {
  search: '',
  address: '',
  floor: '',
  name: '',
};

export function useZoneFilters({ tabKey, buildingId }: UseZoneFiltersArgs) {
  const [filtersByTab, setFiltersByTab] = useState<Record<TabKey, FilterState>>({
    active: { ...EMPTY_FILTER },
    inactive: { ...EMPTY_FILTER },
  });

  const activeTabKey: TabKey = tabKey ?? 'active';
  const filters = filtersByTab[activeTabKey];

  const setSearch = useCallback(
    (value: string) => {
      if (!tabKey) return;
      setFiltersByTab((prev) => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], search: value },
      }));
    },
    [tabKey],
  );

  const setFilters = useCallback(
    (updated: Partial<Omit<FilterState, 'search'>>) => {
      if (!tabKey) return;
      setFiltersByTab((prev) => ({
        ...prev,
        [tabKey]: { ...prev[tabKey], ...updated },
      }));
    },
    [tabKey],
  );

  const { loading, activeZones, inactiveZones, filteredActiveZones, filteredInactiveZones, counts } = useZoneItems({
    buildingId,
    activeFilters: filtersByTab.active,
    inactiveFilters: filtersByTab.inactive,
  });

  const zones = activeTabKey === 'active' ? activeZones : inactiveZones;
  const filteredZones = activeTabKey === 'active' ? filteredActiveZones : filteredInactiveZones;

  return {
    loading,
    zones,
    filteredZones,
    filters,
    setSearch,
    setFilters,
    counts,
  };
}
